// src/pages/AgentDocuments.jsx
import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_AGENT_DOCUMENTS, UPLOAD_AGENT_DOCUMENT, DELETE_AGENT_DOCUMENT, UPDATE_AGENT_BANKING_DETAILS } from '../graphql/agents';

const AgentDocuments = () => {
  const { id } = useParams();
  const [uploadingFile, setUploadingFile] = useState(null);
  const [bankingDetails, setBankingDetails] = useState({
    bankName: '',
    accountNumber: '',
    accountType: '',
    branchCode: '',
    accountHolder: ''
  });
  const [isEditingBanking, setIsEditingBanking] = useState(false);
  
  const { loading, error, data, refetch } = useQuery(GET_AGENT_DOCUMENTS, {
    variables: { id },
    fetchPolicy: 'network-only'
  });

  const [uploadDocument] = useMutation(UPLOAD_AGENT_DOCUMENT, {
    onCompleted: () => {
      setUploadingFile(null);
      refetch();
    },
    onError: (error) => {
      console.error('Error uploading document:', error);
      alert('Failed to upload document. Please try again.');
      setUploadingFile(null);
    }
  });

  const [deleteDocument] = useMutation(DELETE_AGENT_DOCUMENT, {
    onCompleted: () => refetch(),
    onError: (error) => {
      console.error('Error deleting document:', error);
      alert('Failed to delete document. Please try again.');
    }
  });

  const [updateBankingDetails] = useMutation(UPDATE_AGENT_BANKING_DETAILS, {
    onCompleted: () => {
      setIsEditingBanking(false);
      refetch();
    },
    onError: (error) => {
      console.error('Error updating banking details:', error);
      alert('Failed to update banking details. Please try again.');
    }
  });

  React.useEffect(() => {
    if (data?.agent?.bankingDetailsInfo) {
      setBankingDetails(data.agent.bankingDetailsInfo);
    }
  }, [data]);

  const handleFileUpload = (event, type) => {
    const file = event.target.files[0];
    if (file) {
      setUploadingFile(type);
      uploadDocument({
        variables: {
          agentId: id,
          file: file,
          type: type
        }
      });
    }
  };

  const handleDeleteDocument = (documentId) => {
    if (window.confirm('Are you sure you want to delete this document?')) {
      deleteDocument({ variables: { id: documentId } });
    }
  };

  const handleBankingDetailsSubmit = (e) => {
    e.preventDefault();
    updateBankingDetails({
      variables: {
        agentId: id,
        input: bankingDetails
      }
    });
  };

  const handleBankingDetailsChange = (field, value) => {
    setBankingDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleDateString('en-GB');
  };

  const getDocumentsByType = (type) => {
    return data?.agent?.documents?.filter(doc => doc.type === type) || [];
  };

  if (loading) return (
    <div className="flex justify-center items-center h-64">
      <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full text-blue-600" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  );

  if (error) return (
    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
      <strong className="font-bold">Error!</strong>
      <span className="block sm:inline"> {error.message}</span>
    </div>
  );

  const { agent } = data;

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold flex items-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          Agent Documents
        </h1>
        <div className="flex space-x-3">
          <Link to={`/agents/${id}`} className="flex items-center text-blue-600 hover:text-blue-800">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Agent Details
          </Link>
        </div>
      </div>

      {/* Agent Info */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6 flex items-center">
          <div className="bg-yellow-100 text-yellow-700 rounded-full h-16 w-16 flex items-center justify-center text-2xl font-bold mr-4">
            {agent.firstName.charAt(0)}{agent.lastName.charAt(0)}
          </div>
          <div>
            <h3 className="text-xl font-semibold text-gray-900">{agent.firstName} {agent.lastName}</h3>
            <p className="mt-1 text-sm text-gray-500">{agent.email}</p>
          </div>
        </div>
      </div>

      {/* Banking Details */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
        <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Banking Details</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Agent banking information for payments
            </p>
          </div>
          <button
            onClick={() => setIsEditingBanking(!isEditingBanking)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
            {isEditingBanking ? 'Cancel' : 'Edit'}
          </button>
        </div>
        
        <div className="border-t border-gray-200 px-4 py-5 sm:px-6">
          {isEditingBanking ? (
            <form onSubmit={handleBankingDetailsSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                  <input
                    type="text"
                    value={bankingDetails.bankName}
                    onChange={(e) => handleBankingDetailsChange('bankName', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder</label>
                  <input
                    type="text"
                    value={bankingDetails.accountHolder}
                    onChange={(e) => handleBankingDetailsChange('accountHolder', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                  <input
                    type="text"
                    value={bankingDetails.accountNumber}
                    onChange={(e) => handleBankingDetailsChange('accountNumber', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                  <select
                    value={bankingDetails.accountType}
                    onChange={(e) => handleBankingDetailsChange('accountType', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Account Type</option>
                    <option value="savings">Savings</option>
                    <option value="current">Current</option>
                    <option value="transmission">Transmission</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Branch Code</label>
                  <input
                    type="text"
                    value={bankingDetails.branchCode}
                    onChange={(e) => handleBankingDetailsChange('branchCode', e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setIsEditingBanking(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  Save Banking Details
                </button>
              </div>
            </form>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="text-sm font-medium text-gray-500">Bank Name</dt>
                <dd className="mt-1 text-sm text-gray-900">{agent.bankingDetailsInfo?.bankName || 'Not provided'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Account Holder</dt>
                <dd className="mt-1 text-sm text-gray-900">{agent.bankingDetailsInfo?.accountHolder || 'Not provided'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Account Number</dt>
                <dd className="mt-1 text-sm text-gray-900">{agent.bankingDetailsInfo?.accountNumber || 'Not provided'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Account Type</dt>
                <dd className="mt-1 text-sm text-gray-900">{agent.bankingDetailsInfo?.accountType || 'Not provided'}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Branch Code</dt>
                <dd className="mt-1 text-sm text-gray-900">{agent.bankingDetailsInfo?.branchCode || 'Not provided'}</dd>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Documents Section */}
      <div className="space-y-6">
        {/* FICA Documents */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">FICA Documents</h3>
              <p className="mt-1 text-sm text-gray-500">Financial Intelligence Centre Act compliance documents</p>
            </div>
            <div className="flex items-center space-x-3">
              <label className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {uploadingFile === 'fica' ? 'Uploading...' : 'Upload FICA'}
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(e, 'fica')}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  disabled={uploadingFile === 'fica'}
                />
              </label>
            </div>
          </div>
          <div className="border-t border-gray-200">
            {getDocumentsByType('fica').length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {getDocumentsByType('fica').map(document => (
                  <li key={document.id} className="px-4 py-4 sm:px-6 flex items-center justify-between">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{document.name}</p>
                        <p className="text-sm text-gray-500">Uploaded on {formatDate(document.uploadedAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <a
                        href={document.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        View
                      </a>
                      <button
                        onClick={() => handleDeleteDocument(document.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-5 text-center text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2 text-sm">No FICA documents uploaded yet</p>
              </div>
            )}
          </div>
        </div>

        {/* ID Documents */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">ID Documents</h3>
              <p className="mt-1 text-sm text-gray-500">Identity verification documents</p>
            </div>
            <div className="flex items-center space-x-3">
              <label className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {uploadingFile === 'id' ? 'Uploading...' : 'Upload ID'}
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(e, 'id')}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  disabled={uploadingFile === 'id'}
                />
              </label>
            </div>
          </div>
          <div className="border-t border-gray-200">
            {getDocumentsByType('id').length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {getDocumentsByType('id').map(document => (
                  <li key={document.id} className="px-4 py-4 sm:px-6 flex items-center justify-between">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{document.name}</p>
                        <p className="text-sm text-gray-500">Uploaded on {formatDate(document.uploadedAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <a
                        href={document.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        View
                      </a>
                      <button
                        onClick={() => handleDeleteDocument(document.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-5 text-center text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2 text-sm">No ID documents uploaded yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Other Documents */}
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-4 py-5 sm:px-6 flex justify-between items-center">
            <div>
              <h3 className="text-lg leading-6 font-medium text-gray-900">Other Documents</h3>
              <p className="mt-1 text-sm text-gray-500">Additional agent-related documents</p>
            </div>
            <div className="flex items-center space-x-3">
              <label className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md shadow-sm text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 cursor-pointer">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                {uploadingFile === 'other' ? 'Uploading...' : 'Upload Document'}
                <input
                  type="file"
                  onChange={(e) => handleFileUpload(e, 'other')}
                  className="hidden"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  disabled={uploadingFile === 'other'}
                />
              </label>
            </div>
          </div>
          <div className="border-t border-gray-200">
            {getDocumentsByType('other').length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {getDocumentsByType('other').map(document => (
                  <li key={document.id} className="px-4 py-4 sm:px-6 flex items-center justify-between">
                    <div className="flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{document.name}</p>
                        <p className="text-sm text-gray-500">Uploaded on {formatDate(document.uploadedAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <a
                        href={document.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm"
                      >
                        View
                      </a>
                      <button
                        onClick={() => handleDeleteDocument(document.id)}
                        className="text-red-600 hover:text-red-800 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="px-4 py-5 text-center text-gray-500">
                <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="mt-2 text-sm">No other documents uploaded yet</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentDocuments;